/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useRef, useState } from "react";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import { sidebarStructure } from "./structure";
import { useLocation, useNavigate } from "react-router-dom";
import { ScrollText, LayoutDashboard, UsersRound, Eye, Menu, X, LogOut, ChevronLeft } from "lucide-react";
import { useAuthStore } from "hooks";
import sergiImg from "images/avatars/sergiImg.jpeg";

interface SidebarProps {
  setExpand: (value: boolean) => void;
}

const Sidebar: FC<SidebarProps> = ({ setExpand }) => {
  const { startLogout, user } = useAuthStore();
  const username = "Hola " + (user?.name ?? "");
  const company = "Karaoke Rooms Barcelona";
  const profilePic = sergiImg;

  const link = "/";

  const [openedMenu, setOpenedMenu] = useState<Record<string, any>>({});
  const [activeName, setActiveName] = useState("");
  const activeLink = window.location.pathname;

  const listRef = useRef<Record<string, HTMLUListElement | null>>({});

  const [isExpand, setIsExpand] = useState(true);
  const [isExpandOnHover, setIsExpandOnHover] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // eslint-disable-next-line
  const location = useLocation();
  const navigate = useNavigate();

  // En md+ las etiquetas dependen de expandido/hover; en móvil el drawer va siempre completo.
  const showLabels = isExpand || isExpandOnHover || mobileOpen;
  const width = mobileOpen ? "w-72" : isExpand ? "w-72" : isExpandOnHover ? "w-72" : "w-20";

  const handleHoverExpand = (value: boolean) => {
    if (!isExpand) {
      setIsExpandOnHover(value);
    }
  };

  const handleNavigate = (path: string) => {
    setActiveName(path);
    navigate("/" + path);
    setMobileOpen(false);
  };

  const handleToggle = (name: string) => {
    const rootEl = name.split(".")[0];

    if (openedMenu[name]?.open === true) {
      setOpenedMenu((prevState) => ({
        ...prevState,
        [name]: { open: false, height: "0px" },
        [rootEl]: {
          open: rootEl === name ? false : true,
          height: `${(listRef.current[rootEl]?.scrollHeight || 0) - (listRef.current[name]?.scrollHeight || 0)}px`,
        },
      }));
    } else {
      setOpenedMenu((prevState) => ({
        ...prevState,
        [name]: { open: true, height: `${listRef.current[name]?.scrollHeight || 0}px` },
        [rootEl]: {
          open: true,
          height: `${(listRef.current[rootEl]?.scrollHeight || 0) + (listRef.current[name]?.scrollHeight || 0)}px`,
        },
      }));
    }
  };

  const generateIcon = (icon: string) => {
    const icons_map: Record<string, JSX.Element> = {
      dasbor: <LayoutDashboard className="h-5 w-5" />,
      reporte: <ScrollText className="h-5 w-5" />,
      users: <UsersRound className="h-5 w-5" />,
      reservas: <Eye className="h-5 w-5" />,
    };
    return icons_map[icon];
  };

  const generateMenu = (item: any, index: number, recursive: number = 0) => {
    if (activeName === "" && activeLink.includes(item.link)) {
      setActiveName(item.name);
    }
    const isActive = activeName === item.name || activeName.split(".")[0] === item.name;
    const classesActive = activeName === item.name ? "active" : "";

    return (
      <li key={index}>
        <a
          role="button"
          tabIndex={0}
          id={item.id}
          onClick={() => {
            if ("child" in item) {
              handleToggle(item.name);
            } else if ("link" in item) {
              handleNavigate(item.name);
            }
          }}
          onKeyDown={(event) => {
            const { code } = event;
            if (code === "Space") {
              if ("child" in item) {
                handleToggle(item.name);
              } else if ("link" in item) {
                handleNavigate(item.name);
              }
            }
          }}
          className={[
            "group m-0 mb-1 flex h-12 cursor-pointer items-center justify-between rounded-xl py-0 pr-3 transition focus:outline-none",
            recursive === 0 ? "pl-4" : recursive === 1 ? "pl-11" : "pl-16",
            isActive
              ? "bg-violet-500/15 font-semibold text-violet-200 ring-1 ring-inset ring-violet-400/30"
              : "text-slate-400 hover:bg-white/5 hover:text-white",
            classesActive,
          ].join(" ")}
        >
          <div className="flex items-center gap-3">
            {item.icon ? (
              item.icon === "dot" ? (
                <div className="flex h-3 w-3 items-center justify-center">
                  <div className={[`${classesActive ? "h-2 w-2" : "h-1 w-1"}`, "rounded-full bg-current duration-200"].join(" ")}></div>
                </div>
              ) : (
                generateIcon(item.icon)
              )
            ) : null}
            <div className={`truncate ${showLabels ? "" : "h-0 w-0 opacity-0"}`}>{item.title}</div>
          </div>
          {"child" in item ? (
            <div className={`${showLabels ? "" : "h-0 w-0 opacity-0"}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          ) : (
            false
          )}
        </a>
        {"child" in item ? (
          <ul
            ref={(el) => (listRef.current[item.name] = el)}
            className={["overflow-hidden duration-300 ease-in-out", showLabels ? "" : "h-0"].join(" ")}
            style={{ maxHeight: `${openedMenu[item.name]?.height || "0px"}` }}
            key={item.name}
          >
            {item.child.map((value: any, idx: number) => generateMenu(value, idx, recursive + 1))}
          </ul>
        ) : (
          false
        )}
      </li>
    );
  };

  return (
    <>
      {/* Botón hamburguesa (solo móvil) */}
      <button
        className="fixed left-4 top-4 z-[60] grid h-11 w-11 place-items-center rounded-xl border border-white/10 bg-[#0c0a16]/80 text-white shadow-lg backdrop-blur-xl transition hover:bg-white/10 md:hidden"
        onClick={() => setMobileOpen(true)}
        aria-label="Abrir menú"
      >
        <Menu size={20} />
      </button>

      {/* Backdrop (solo móvil) */}
      {mobileOpen && (
        <button
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="Cerrar menú"
        />
      )}

      <nav
        role="navigation"
        className={[
          "fixed inset-y-0 left-0 z-50 duration-300 ease-in-out",
          "border-r border-white/10 bg-[#0c0a16]/90 backdrop-blur-xl",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          "md:translate-x-0",
          width,
        ].join(" ")}
      >
        {/* Cerrar (solo móvil) */}
        <button
          className="absolute right-3 top-3 z-50 grid h-9 w-9 place-items-center rounded-lg text-slate-300 transition hover:bg-white/10 hover:text-white md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="Cerrar menú"
        >
          <X size={18} />
        </button>

        {/* Colapsar/expandir (solo md+) */}
        <button
          className="absolute -right-3 top-28 z-50 hidden h-6 w-6 place-items-center rounded-full border border-white/15 bg-[#1a1530] text-slate-300 shadow-lg transition hover:text-white md:grid"
          onClick={() => {
            setIsExpand(!isExpand);
            setExpand(!isExpand);
          }}
          aria-label={isExpand ? "Colapsar menú" : "Expandir menú"}
        >
          <ChevronLeft size={16} className={`${isExpand ? "rotate-0" : "rotate-180"} transform duration-500`} />
        </button>

        <div onMouseEnter={() => handleHoverExpand(true)} onMouseLeave={() => handleHoverExpand(false)} className="relative h-screen overflow-hidden">
          <SimpleBar style={{ height: "100%" }} autoHide>
            <div className="mt-16 text-slate-300">
              <div className="my-8 flex h-56 flex-col items-center overflow-x-hidden">
                <a href={link} className="flex flex-col items-center justify-center text-center">
                  <div
                    className={`overflow-hidden rounded-full border-2 border-white/15 ring-2 ring-violet-500/20 duration-300 ${
                      showLabels ? "h-24 w-24" : "h-12 w-12"
                    }`}
                  >
                    <img src={profilePic} className="block h-full w-full object-cover" alt="" />
                  </div>
                  <div className={`mt-3 truncate text-base font-semibold text-white duration-300 ${showLabels ? "" : "h-0 w-0 opacity-0"}`}>{username}</div>
                  <div className={`truncate text-sm text-slate-400 duration-300 ${showLabels ? "" : "h-0 w-0 opacity-0"}`}>{company}</div>
                </a>
                <button
                  className="mt-4 flex items-center gap-2 rounded-lg px-3 py-2 text-slate-300 transition hover:bg-white/5 hover:text-white focus:outline-none"
                  onClick={startLogout}
                >
                  <LogOut className="h-5 w-5 text-slate-400" aria-hidden="true" />
                  <span className={`text-sm font-medium ${showLabels ? "" : "hidden"}`}>Salir</span>
                </button>
              </div>

              <div className="mb-10 mt-3 p-0">
                <ul className="list-none px-3 text-sm font-normal">{sidebarStructure.map((item, index) => generateMenu(item, index))}</ul>
              </div>
            </div>
          </SimpleBar>
        </div>
      </nav>
    </>
  );
};

export default Sidebar;
